import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { DAY_STATE_LABEL, DAY_STATE_STYLE, DayState, TIME_SLOTS, todayKst } from "./holidayUtils";

type HolidayToolbarProps = {
    month: Date;
    onMonthChange: (month: Date) => void;
    defaultEndTime: string;
    onSaveDefaultEndTime: (value: string) => void;
    isSavingConfig: boolean;
    syncYearOptions: number[];
    onSync: (year: number) => void;
    isSyncing: boolean;
};

const selectTriggerClass =
    "relative shadow-none text-[#1B1C1E] text-[15px] flex gap-[8px] rounded-[5px] focus:ring-2 border border-[#E1E2E4] bg-white h-[44px]";
const buttonClass = "h-[44px] px-[14px] rounded-[5px] text-[14px] whitespace-nowrap disabled:opacity-50";
const LEGEND: DayState[] = ["public", "manual", "early", "weekend"];

// 월 이동 · 기본 업무 종료시간 · 공휴일 불러오기 · 범례
export default function HolidayToolbar({
    month,
    onMonthChange,
    defaultEndTime,
    onSaveDefaultEndTime,
    isSavingConfig,
    syncYearOptions,
    onSync,
    isSyncing,
}: HolidayToolbarProps) {
    const [endTime, setEndTime] = useState(defaultEndTime);
    const [syncYear, setSyncYear] = useState(String(month.getFullYear()));

    useEffect(() => setEndTime(defaultEndTime), [defaultEndTime]);
    useEffect(() => {
        if (syncYearOptions.includes(month.getFullYear())) setSyncYear(String(month.getFullYear()));
    }, [month, syncYearOptions]);

    const moveMonth = (diff: number) => onMonthChange(new Date(month.getFullYear(), month.getMonth() + diff, 1));
    // 오늘 이동도 KST 기준 (페이지 초기 월과 동일)
    const today = todayKst();

    return (
        <div className="flex flex-col gap-[14px]">
            <div className="flex flex-wrap items-center justify-between gap-[12px]">
                {/* 월 이동 */}
                <div className="flex items-center gap-[8px]">
                    <button type="button" aria-label="이전 달" onClick={() => moveMonth(-1)} className={twMerge(buttonClass, "border border-[#E1E2E4] px-[12px]")}>
                        ◀
                    </button>
                    <h3 className="text-[20px] font-[700] text-[#222222] min-w-[120px] text-center">
                        {month.getFullYear()}년 {month.getMonth() + 1}월
                    </h3>
                    <button type="button" aria-label="다음 달" onClick={() => moveMonth(1)} className={twMerge(buttonClass, "border border-[#E1E2E4] px-[12px]")}>
                        ▶
                    </button>
                    <button
                        type="button"
                        onClick={() => onMonthChange(new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, 1))}
                        className={twMerge(buttonClass, "border border-[#E1E2E4]")}
                    >
                        오늘
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-[8px]">
                    {/* 기본 업무 종료시간 */}
                    <span className="text-[14px] text-[#666666] whitespace-nowrap">기본 업무 종료</span>
                    <Select value={endTime} onValueChange={setEndTime}>
                        <SelectTrigger className={twMerge(selectTriggerClass, "w-[104px]")}>
                            <SelectValue placeholder="시간" />
                            <ArrowDown className="[&_path]:fill-[#666666]" />
                        </SelectTrigger>
                        <SelectContent className="bg-white max-h-[280px]">
                            {TIME_SLOTS.map(t => (
                                <SelectItem key={t} value={t}>
                                    {t}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <button
                        type="button"
                        disabled={isSavingConfig || endTime === defaultEndTime}
                        onClick={() => onSaveDefaultEndTime(endTime)}
                        className={twMerge(buttonClass, "bg-black text-white")}
                    >
                        저장
                    </button>

                    {/* 공휴일 불러오기 */}
                    <Select value={syncYear} onValueChange={setSyncYear}>
                        <SelectTrigger className={twMerge(selectTriggerClass, "w-[104px] sm:ml-[12px]")}>
                            <SelectValue placeholder="연도" />
                            <ArrowDown className="[&_path]:fill-[#666666]" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                            {syncYearOptions.map(y => (
                                <SelectItem key={y} value={String(y)}>
                                    {y}년
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <button
                        type="button"
                        disabled={isSyncing}
                        onClick={() => onSync(Number(syncYear))}
                        className={twMerge(buttonClass, "bg-[#4F46E5] text-white")}
                    >
                        {isSyncing ? "불러오는 중…" : "공휴일 불러오기"}
                    </button>
                </div>
            </div>

            {/* 범례 */}
            <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[6px] text-[13px] text-[#666666]">
                {LEGEND.map(state => (
                    <span key={state} className="flex items-center gap-[6px]">
                        <span className={twMerge("w-[10px] h-[10px] rounded-[2px]", DAY_STATE_STYLE[state].dot)} />
                        {DAY_STATE_LABEL[state]}
                    </span>
                ))}
                <span className="text-[#999999]">· <b className="text-[#4F46E5]">+</b> 를 눌러 휴무일 등록, 등록된 날짜를 눌러 수정·삭제</span>
            </div>
        </div>
    );
}
